import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { gsap } from "gsap/dist/gsap";
import React, { useEffect } from "react";

import { BigHeroBlock, Context, constants } from "@reactivated";

import { MetaTagsBlock } from "@thelabnyc/thelabui/src/components/MetaTagsBlock";
import { throttle } from "@thelabnyc/thelabui/src/utils/functional";

import { FooterNavigation } from "../FooterNavigation";
import { HeaderNavigation, Overlap } from "../HeaderNavigation";

import styles from "./index.module.scss";

import "../../styles/global.scss";

gsap.registerPlugin(ScrollTrigger);

interface GenericBlock {
    type: string;
    value: object | string;
    id: string;
}

type BackgroundThemeChoices =
    (typeof constants)["server.cms.types.BackgroundThemeChoices"][keyof (typeof constants)["server.cms.types.BackgroundThemeChoices"]];

/**
 * Created by looking at the generated page body interfaces and creating an
 * interface that covers every current use case
 */
interface GenericBody {
    _type: "StreamField";
    _variant: string;
    value: GenericBlock[];
    [k: string]: unknown;
}

interface Props {
    title: string;
    seo_title?: string;
    search_description?: string;
    robots?: string;
    rootClassName?: string;
    /**
     * Any page of ours will have props.page.body but will have a different type
     * for it, like CmsHomePage. We need a generic version for overlapNav.
     */
    body?: GenericBody;
    children?: React.ReactNode;
    /**
     * Should be BackgroundThemeChoices but reactivated doesn't support that yet
     */
    color_theme?: string;
    sticky_nav?: boolean;
}

export const PageSkeleton = ({ sticky_nav = true, ...props }: Props) => {
    const context = React.useContext(Context);

    /**
     * Layout shifts can make scroll animations jumpy. This attempts to fix that
     */
    useEffect(() => {
        const refresh = throttle(() => {
            ScrollTrigger.refresh();
        }, 100);

        const resizeObserver = new ResizeObserver(refresh);

        resizeObserver.observe(document.body);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    /**
     * Sanity check in case GenericBody's assumptions don't hold and/or the
     * page doesn't have a `body` property at all
     */
    const bodyProp =
        props.body?._type === "StreamField" && Array.isArray(props.body.value)
            ? props.body
            : null;
    const type = bodyProp?.value[0]?.type || "";

    /**
     * If the first block in a body StreamField is a big_hero then we want the
     * nav to overlap, and we want the nav to use big_hero's text_color.
     */
    let overlapNav: Overlap = null;
    if (type === "big_hero") {
        const value = bodyProp?.value[0].value as unknown as BigHeroBlock;
        if (value.text_color) {
            overlapNav = value.text_color;
        }
    }

    const theme = (props.color_theme ?? "dark") as BackgroundThemeChoices;

    return (
        <html
            lang="en-US"
            className={theme === "dark" ? styles.rootDark : styles.rootLight}
            data-release={context.CI_COMMIT_SHA}
        >
            <head>
                <meta charSet="utf-8" />
                <title>{props.seo_title || props.title}</title>
                {props.search_description && (
                    <meta
                        name="description"
                        content={props.search_description}
                    />
                )}
                {props.robots && <meta name="robots" content={props.robots} />}
                <MetaTagsBlock tags={context.meta_tags.meta_tags.value} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1"
                />
                <link
                    rel="icon"
                    type="image/png"
                    href={context.core_assets.favicon_png96}
                    sizes="96x96"
                />
                <link
                    rel="icon"
                    type="image/svg+xml"
                    href={context.core_assets.favicon_svg}
                />
                <link
                    rel="shortcut icon"
                    href={context.core_assets.favicon_ico}
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={context.core_assets.apple_touch_icon}
                />
            </head>
            <body>
                <div className={props.rootClassName}>
                    {context.header_nav && (
                        <HeaderNavigation
                            header_nav={context.header_nav}
                            overlapNav={overlapNav}
                            sticky={sticky_nav}
                        />
                    )}
                    {props.children}
                    {context.footer_nav && (
                        <FooterNavigation footer_nav={context.footer_nav} />
                    )}
                </div>
            </body>
        </html>
    );
};
